import React, { useState } from "react";
import {
  Grid,
  Typography,
  Box,
  TextField,
  Button,
  Snackbar,
  Alert,
  Divider,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import emailjs from "emailjs-com";

const Contactform: React.FC = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
    agree: false,
  });

  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
    agree: "",
  });

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;

    let checked: boolean | undefined;
    if (type === "checkbox") {
      checked = (e.target as HTMLInputElement).checked; // Cast to HTMLInputElement
    }

    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });

    // Clear error for the current field on change
    setErrors({
      ...errors,
      [name]: "",
    });
  };

  const validateForm = () => {
    let formIsValid = true;
    const newErrors = { ...errors };

    // Validate first name
    if (!formData.firstName) {
      formIsValid = false;
      newErrors.firstName = "First Name is required.";
    }

    // Validate last name
    if (!formData.lastName) {
      formIsValid = false;
      newErrors.lastName = "Last Name is required.";
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email) {
      formIsValid = false;
      newErrors.email = "Email is required.";
    } else if (!emailRegex.test(formData.email)) {
      formIsValid = false;
      newErrors.email = "Invalid email format.";
    }

    // Validate phone (optional, but can add more rules)
    if (!formData.phone) {
      newErrors.phone = "Phone Number is required.";
    }

    // Validate message
    if (!formData.message) {
      formIsValid = false;
      newErrors.message = "Message cannot be empty.";
    }

    // Validate agreement
    if (!formData.agree) {
      formIsValid = false;
      newErrors.agree = "You must agree to the terms and conditions.";
    }

    setErrors(newErrors);
    return formIsValid;
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validate the form
    if (!validateForm()) {
      setSnackbarMessage("Please fix the errors in the form.");
      setSnackbarOpen(true);
      return;
    }

    // Send email using EmailJS
    emailjs.send(
      process.env.REACT_APP_EMAILJS_SERVICE_ID!,
      process.env.REACT_APP_EMAILJS_TEMPLATE_ID!,
      formData,
      process.env.REACT_APP_EMAILJS_USER_ID!
    )
    .then((response) => {
      setSnackbarMessage("Message sent successfully!");
      setSnackbarOpen(true);
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        message: "",
        agree: false,
      });
    }, (err) => {
      setSnackbarMessage("Failed to send message. Please try again.");
      setSnackbarOpen(true);
    });
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box sx={{ flexGrow: 1, padding: { xs: 2, sm: 3, md: 5 } }}>
      <Grid container spacing={0} component="form" onSubmit={handleSubmit} >
        {/* Left side */}
        <Grid item xs={12} md={8} className='component'>
          <Box sx={{ backgroundColor: "#f3f4f6", padding: { xs: 2, md: 5 }, color: "black" }}>
            <Typography
              component="h1"
              className='component'
              sx={{
                backgroundColor: "#f97316",
                color: "white",
                borderRadius: "10px",
                padding: "5px 7px",
                fontSize: { xs: "13px", sm: "15px" },
                textTransform: "none",
                display: 'inline-block',
              }}
            >
              Contact Us
            </Typography>

            <Typography variant="h4" sx={{ marginTop: '20px', color: "#172554", fontSize: { xs: "24px", md: "30px" } }} className='component'>
              Get In Touch
            </Typography>

            <Grid container spacing={2} sx={{ paddingTop: 4 }} className='component'>
              {/* First two inputs */}
              <Grid item xs={12} md={6}>
                <Typography sx={{ fontSize: { xs: '14px', md: '15px' }, marginBottom: 1 }}>First Name</Typography>
                <TextField
                  fullWidth
                  size="medium"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  InputProps={{
                    sx: {
                      backgroundColor: "white",
                      "& fieldset": { borderColor: "black" },
                      "&:hover fieldset": { borderColor: "black" },
                      "&.Mui-focused fieldset": { borderColor: "black" },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography sx={{ fontSize: { xs: '14px', md: '15px' }, marginBottom: 1 }}>Last Name</Typography>
                <TextField
                  fullWidth
                  size="medium"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                  InputProps={{
                    sx: {
                      backgroundColor: "white",
                      "& fieldset": { borderColor: "black" },
                      "&:hover fieldset": { borderColor: "black" },
                      "&.Mui-focused fieldset": { borderColor: "black" },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography sx={{ fontSize: { xs: '14px', md: '15px' }, marginBottom: 1 }}>Email Address</Typography>
                <TextField
                  fullWidth
                  size="medium"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  error={!!errors.email}
                  helperText={errors.email}
                  InputProps={{
                    sx: {
                      backgroundColor: "white",
                      "& fieldset": { borderColor: "black" },
                      "&:hover fieldset": { borderColor: "black" },
                      "&.Mui-focused fieldset": { borderColor: "black" },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography sx={{ fontSize: { xs: '14px', md: '15px' }, marginBottom: 1 }}>Phone Number</Typography>
                <TextField
                  fullWidth
                  size="medium"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  error={!!errors.phone}
                  helperText={errors.phone}
                  InputProps={{
                    sx: {
                      backgroundColor: "white",
                      "& fieldset": { borderColor: "black" },
                      "&:hover fieldset": { borderColor: "black" },
                      "&.Mui-focused fieldset": { borderColor: "black" },
                    },
                  }}
                />
              </Grid>

              {/* Full-width textarea */}
              <Grid item xs={12}>
                <Typography sx={{ fontSize: { xs: '14px', md: '15px' }, marginBottom: 1 }}>Message</Typography>
                <TextField
                  fullWidth
                  size="medium"
                  multiline
                  rows={4}
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  error={!!errors.message}
                  helperText={errors.message}
                  InputProps={{
                    sx: {
                      backgroundColor: "white",
                      "& fieldset": { borderColor: "black" },
                      "&:hover fieldset": { borderColor: "black" },
                      "&.Mui-focused fieldset": { borderColor: "black" },
                    },
                  }}
                />
              </Grid>

              {/* Checkbox for agreement */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="agree"
                      checked={formData.agree}
                      onChange={handleChange}
                      color="primary"
                    />
                  }
                  label="I agree to the terms and conditions"
                />
                {errors.agree && <Typography sx={{ color: 'red' }}>{errors.agree}</Typography>}
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  sx={{
                    backgroundColor: "#f97316",
                    color: "white",
                    "&:hover": { backgroundColor: "#f97316" },
                  }}
                >
                  Submit
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Grid>

        {/* Right side: Placeholder for any additional content */}
        <Grid item xs={12} md={4} className='component'>
          <Box sx={{ padding: { xs: 2, md: 4 }, height: "100%" }} className='component'>
            <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: "100%" }} className='component'>
              <Typography variant="h1" sx={{ fontSize: { xs: "20px", md: "25px" }, marginBottom: 1, color: "#172554" }} className='component'>
                <i className="fa-solid fa-headset" style={{ color: "#f97316" }}></i> Support request
              </Typography>
              <Typography className='component'>Our experts are ready to answer your questions.</Typography>
              <Divider sx={{ marginBottom: 2 }} />

              <Typography variant="h2" sx={{ fontSize: { xs: "20px", md: "25px" }, marginBottom: 1, color: "#172554" }} className='component'>
                <i className="fa-solid fa-circle-question" style={{ color: "#f97316" }}></i> Need Help
              </Typography>
              <Typography className='component'>For technical questions, please contact Customer Care.</Typography>
              <Divider sx={{ marginBottom: 2 }} />

              <Typography variant="h3" sx={{ fontSize: { xs: "20px", md: "25px" }, marginBottom: 1, color: "#172554" } } className='component'>
                <i className="fa-solid fa-circle-info" style={{ color: "#f97316" }}></i> Needs More Info?
              </Typography>
              <Typography className='component'>For technical or billing questions, please contact Customer Care.</Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Snackbar for messages */}
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity="success">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Contactform;
